import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  vatReturnQuerySchema,
  vatReturnExportQuerySchema,
  vatReturnReportSchema,
  vatReturnReviewPreviewSchema,
  vatReturnReviewSchema,
  type VatReturnQuery,
  type VatReturnReviewInput,
  type VatReturnReviewCreate,
} from '@erp/shared';
import { useSession } from '@/features/auth';
import { apiDownload, apiFetch, saveBlob } from '@/lib/api-client';

const base = '/api/v1/accounting/ledger/vat-return';
function useVatScope() {
  const session = useSession();
  return {
    key: [
      'vat-return',
      session.data?.tenantId,
      session.data?.id,
      session.data?.roleKey,
      [...(session.data?.permissions ?? [])].sort().join(','),
    ] as const,
    enabled: Boolean(session.data),
  };
}
export function useVatReturn(input: VatReturnQuery, valid: boolean) {
  const scope = useVatScope();
  return useQuery({
    queryKey: [...scope.key, input],
    enabled: scope.enabled && valid,
    staleTime: 0,
    gcTime: 0,
    queryFn: () => {
      const query = vatReturnQuerySchema.parse({ ...input, issuesOnly: String(input.issuesOnly) });
      return apiFetch(
        `${base}?${new URLSearchParams(
          Object.entries(query)
            .filter(([, value]) => value !== undefined)
            .map(([key, value]) => [key, String(value)]),
        )}`,
        vatReturnReportSchema,
      );
    },
  });
}
export function usePreviewVatReview() {
  return useMutation({
    mutationFn: (input: VatReturnReviewInput) =>
      apiFetch(`${base}/reviews/preview`, vatReturnReviewPreviewSchema, {
        method: 'POST',
        body: input,
      }),
  });
}
export function useSaveVatReview() {
  const scope = useVatScope(),
    client = useQueryClient();
  return useMutation({
    mutationFn: (input: VatReturnReviewCreate) =>
      apiFetch(`${base}/reviews`, vatReturnReviewSchema, { method: 'POST', body: input }),
    onSuccess: () => client.invalidateQueries({ queryKey: scope.key }),
  });
}
export function useExportVatReturn() {
  return useMutation({
    mutationFn: (input: VatReturnQuery & { locale: string }) => {
      const query = vatReturnExportQuerySchema.parse({
        ...input,
        issuesOnly: String(input.issuesOnly),
      });
      const parameters = new URLSearchParams(
        Object.entries(query)
          .filter(([, value]) => value !== undefined)
          .map(([key, value]) => [key, String(value)]),
      );
      return apiDownload(`${base}/export?${parameters}`, 'vat-preparation.csv');
    },
    onSuccess: saveBlob,
  });
}
